import { ThreadListItemPrimitive, useAuiState } from '@assistant-ui/react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ButtonBase from '@mui/material/ButtonBase';
import clsx from 'clsx';
import { useState } from 'react';

import { RelativeDate } from '#components/relative-timestamp/relative-date.js';

import type { AgentWorkbenchThreadCustom } from './agent-workbench-thread-list-adapter.js';

interface AgentWorkbenchThreadListItemProps {
	/**
	 * Called after the row is activated, so the caller can dismiss the history
	 * drawer. The conversation id is undefined while the thread's server record
	 * is still being created.
	 */
	onSelectThread?: (conversationId: string | undefined) => void;
	allowDelete?: boolean;
}

/**
 * One row in the conversation list. Reads its own thread from the surrounding
 * `ThreadListItem` runtime scope, so it takes no thread props.
 */
export function AgentWorkbenchThreadListItem({
	onSelectThread,
	allowDelete = true,
}: AgentWorkbenchThreadListItemProps) {
	const isActive = useAuiState((s) => s.threads.mainThreadId === s.threadListItem.id);
	const conversationId = useAuiState((s) => s.threadListItem.remoteId);
	const title = useAuiState((s) => s.threadListItem.title);
	const lastActivityAt = useAuiState((s) => readLastActivityAt(s.threadListItem.custom));
	const [confirmingDelete, setConfirmingDelete] = useState(false);

	return (
		<ThreadListItemPrimitive.Root
			className={clsx(
				'group flex items-stretch rounded transition-colors duration-150 motion-reduce:transition-none',
				isActive ? 'bg-blue-a3' : 'hover:bg-gray-a3',
			)}
			onMouseLeave={() => setConfirmingDelete(false)}
		>
			<ThreadListItemPrimitive.Trigger asChild onClick={() => onSelectThread?.(conversationId)}>
				<ButtonBase className='flex min-w-0 flex-1 items-start gap-2 px-3 py-2 text-left'>
					<span
						aria-hidden
						className={clsx(
							'shrink-0 font-mono leading-normal transition-colors duration-150 select-none [text-box:initial] motion-reduce:transition-none',
							isActive ? 'text-blue-11' : 'text-transparent group-hover:text-gray-a8',
						)}
					>
						❯
					</span>
					<span className='flex min-w-0 flex-1 flex-col gap-0.5'>
						<span
							className={clsx(
								'w-full overflow-hidden leading-normal text-ellipsis whitespace-nowrap [text-box:initial]',
								isActive ? 'text-gray-12' : 'text-gray-11',
							)}
						>
							{title ?? 'Untitled conversation'}
						</span>
						{lastActivityAt ?
							<RelativeDate date={lastActivityAt} className='text-xs leading-normal text-gray-11 [text-box:initial]' />
						:	<span className='text-xs leading-normal text-gray-11 [text-box:initial]'>Just now</span>}
					</span>
				</ButtonBase>
			</ThreadListItemPrimitive.Trigger>

			{allowDelete &&
				(confirmingDelete ?
					<>
						<ThreadListItemPrimitive.Delete asChild>
							<ButtonBase
								className='flex w-8 shrink-0 items-center justify-center text-red-11 hover:bg-red-a3'
								aria-label='Confirm delete conversation'
							>
								<CheckIcon style={{ fontSize: 16 }} />
							</ButtonBase>
						</ThreadListItemPrimitive.Delete>
						<ButtonBase
							onClick={() => setConfirmingDelete(false)}
							className='flex w-8 shrink-0 items-center justify-center text-gray-11 hover:bg-gray-a3 hover:text-gray-12'
							aria-label='Cancel delete'
						>
							<CloseIcon style={{ fontSize: 16 }} />
						</ButtonBase>
					</>
				:	<ButtonBase
						onClick={() => setConfirmingDelete(true)}
						className={clsx(
							'flex w-8 shrink-0 items-center justify-center text-gray-11 transition duration-150 hover:text-red-11 motion-reduce:transition-none',
							'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
						)}
						aria-label='Delete conversation'
					>
						<DeleteOutlineIcon style={{ fontSize: 16 }} />
					</ButtonBase>)}
		</ThreadListItemPrimitive.Root>
	);
}

/**
 * Extracts the thread's last-activity timestamp from its adapter `custom`
 * payload.
 *
 * @param custom - The thread list item's untyped `custom` payload.
 * @returns The last-activity date, or undefined when absent or not a `Date`.
 */
function readLastActivityAt(custom: Record<string, unknown> | undefined): Date | undefined {
	const value = (custom as Partial<AgentWorkbenchThreadCustom> | undefined)?.lastActivityAt;
	return value instanceof Date ? value : undefined;
}
